import { supabase } from "../supabase/supabaseClient";

const ORG_AVATAR_BUCKET = "avatars";
const ORG_AVATAR_MAX_SIZE = 5 * 1024 * 1024;
const ORG_AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"];
const ORG_AVATAR_EXT_MAP = { "image/jpeg": "jpg", "image/png": "png", "image/webp": "webp" };

export async function orgFindByName(q) {
  return supabase.rpc("org_find_by_name", { q });
}

export async function createOrg(payload) {
  return supabase
    .from("org_profiles")
    .insert(payload)
    .select("*")
    .single();
}

export async function updateOrg(orgId, payload) {
  return supabase
    .from("org_profiles")
    .update(payload)
    .eq("id", orgId)
    .select("*")
    .single();
}

export async function getOrgForUser(userId) {
  const { data: membership, error: membershipError } = await supabase
    .from("org_memberships")
    .select("org_id, orgrole, user_id")
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();
  if (membershipError) return { data: null, error: membershipError };
  if (!membership) return { data: null, error: null };

  const { data: org, error: orgError } = await supabase
    .from("org_profiles")
    .select("*")
    .eq("id", membership.org_id)
    .single();

  if (orgError) return { data: null, error: orgError };

  return { data: { org, role: membership.orgrole ? membership.orgrole.toString().toLowerCase() : null, membership }, error: null };
}

export async function getMembers(orgId) {
  return supabase
    .from("org_memberships")
    .select("user_id, orgrole")
    .eq("org_id", orgId);
}

export function getOrgAvatarPublicUrl(orgId, cacheBust) {
  if (!orgId) return "";
  const filePath = `orgs/${orgId}/avatar.webp`;
  const { data } = supabase.storage.from(ORG_AVATAR_BUCKET).getPublicUrl(filePath);
  if (!data?.publicUrl) return "";
  return cacheBust ? `${data.publicUrl}?t=${cacheBust}` : data.publicUrl;
}

export async function uploadOrgAvatar(orgId, file) {
  if (!orgId) {
    return { data: null, error: new Error("Organization id is required.") };
  }

  if (!file) {
    return { data: null, error: new Error("No image selected.") };
  }

  if (!ORG_AVATAR_TYPES.includes(file.type)) {
    return { data: null, error: new Error("Please choose a JPG, PNG, or WebP image.") };
  }

  if (file.size > ORG_AVATAR_MAX_SIZE) {
    return { data: null, error: new Error("Image must be 5 MB or smaller.") };
  }

  const ext = ORG_AVATAR_EXT_MAP[file.type] || "webp";
  const filePath = `orgs/${orgId}/avatar.${ext}`;
  const { error: uploadError } = await supabase.storage
    .from(ORG_AVATAR_BUCKET)
    .upload(filePath, file, { upsert: true, contentType: file.type });

  if (uploadError) {
    return {
      data: null,
      error: new Error(`Storage upload blocked: ${uploadError.message}`),
    };
  }

  const { data: urlData } = supabase.storage.from(ORG_AVATAR_BUCKET).getPublicUrl(filePath);
  const avatarUrl = `${urlData.publicUrl}?t=${Date.now()}`;

  const { data: orgRow, error: updateError } = await supabase
    .from("org_profiles")
    .update({ avatar_url: avatarUrl })
    .eq("id", orgId)
    .select("*")
    .single();

  if (updateError) {
    return {
      data: null,
      error: new Error(`DB update blocked (org_profiles.avatar_url): ${updateError.message}`),
    };
  }

  return {
    data: { avatar_url: avatarUrl, org: orgRow },
    error: null,
  };
}
